import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { chartAlpha, seriesColor } from '../constants/chartPalette';
import { ChartDataPoint, formatValue, shareOfPeak } from '../shared';

/**
 * Four abstract landmasses, for `CHOROPLETH` only.
 *
 * They stand in for regions a choropleth shades. They are **not** drawn behind
 * plotted points: a shape that looks like a coastline and corresponds to
 * nothing is worse than an empty canvas, because the reader takes it for
 * geography and reads the dots against it.
 */
const LANDMASSES = Object.freeze([
  'M 12 18 C 26 8 44 10 52 20 C 58 28 52 38 40 40 C 26 42 14 34 12 26 Z',
  'M 62 14 C 76 8 92 14 96 24 C 100 34 90 42 78 40 C 66 38 58 28 62 20 Z',
  'M 18 48 C 30 42 44 46 48 54 C 52 62 44 70 32 70 C 20 70 12 60 18 52 Z',
  'M 64 50 C 78 44 94 50 98 60 C 102 70 92 74 80 72 C 68 70 60 60 64 54 Z',
]);

/** Dot diameter, in px, between the smallest and the largest `value`. */
const DOT_MIN = 8;
const DOT_MAX = 26;

/**
 * Where records are, and how much is there.
 *
 * ## Two modes, one contract (CT-5641)
 *
 * `MAP` publishes `features[]` and the `mode` that says how to read them:
 *
 * - **`POINTS` / `CLUSTER`** — each feature carries `lat` / `lng` and is
 *   plotted at its own position inside `bounds`, projected linearly. That is
 *   not a projection of the Earth and does not pretend to be: within a city's
 *   bounds the difference does not change which dot is where, and claiming
 *   more would be claiming a basemap this component does not have.
 * - **`CHOROPLETH`** — each feature is a `region_code` with a value, shaded
 *   against the strongest one. The landmasses stand in for the regions.
 *
 * ## The canvas is the widget
 *
 * It used to be a 96×62px thumbnail with the names listed beside it. Five
 * branches in 96px land on top of each other, which throws away the only
 * thing a map contributes — where things are relative to each other — to make
 * room for a list that says it worse.
 *
 * So the plot fills the slot, each point is labelled where it sits, and the
 * dot's diameter carries its `value`. Reading position, magnitude and name in
 * one glance is the whole case for drawing a map instead of a table.
 *
 * A feature with no value gets the middle size rather than the smallest: an
 * unmeasured branch is not a quiet one.
 *
 * @param {object} props
 * @param {object} props.data `MAP`'s `data`, or the pre-v2 `regions[]`.
 * @param {string} [props.accent] Overrides the hue.
 * @param {string} [props.className]
 */
function MapChartWidget({ data, accent, className = '' }) {
  const features = useMemo(() => {
    if (data?.features) {
      return data.features.map((feature, index) => ({
        key: feature.key ?? feature.record_uuid ?? feature.label ?? String(index),
        label: feature.label ?? feature.region_code ?? '',
        value: feature.value ?? null,
        lat: feature.lat,
        lng: feature.lng,
      }));
    }
    return (data?.regions ?? []).map((region) => ({
      key: region.label,
      label: region.label,
      value: region.value,
      display: region.display,
    }));
  }, [data]);

  const isPoints = data?.mode === 'POINTS' || data?.mode === 'CLUSTER';
  const format = data?.format ?? null;

  const share = useMemo(
    () => shareOfPeak(features.map((feature) => feature.value ?? 0)),
    [features],
  );

  if (!features.length) return null;

  const hue = seriesColor(0, accent);
  const shown = (feature) => (
    feature.display ?? (feature.value == null ? '' : formatValue(feature.value, format))
  );

  /** A feature's position inside `bounds`, as a percentage of the plot. */
  const place = (feature) => {
    const b = data?.bounds;
    if (!b || typeof feature.lat !== 'number' || typeof feature.lng !== 'number') return null;
    const spanX = (b.east - b.west) || 1;
    const spanY = (b.north - b.south) || 1;
    return {
      left: ((feature.lng - b.west) / spanX) * 100,
      top: ((b.north - feature.lat) / spanY) * 100,
    };
  };

  if (isPoints) {
    return (
      <div className={`flex h-full min-h-0 flex-col ${className}`.trim()}>
        {/* Inset so a dot at the very edge of `bounds` is not half-clipped,
            and its label has somewhere to go. */}
        <div className="relative min-h-[120px] flex-1 rounded-md bg-surface-muted">
          <div className="absolute inset-[10%]">
            {features.map((feature) => {
              const at = place(feature);
              if (!at) return null;
              const size = feature.value == null
                ? (DOT_MIN + DOT_MAX) / 2
                : DOT_MIN + share(feature.value) * (DOT_MAX - DOT_MIN);

              return (
                <span
                  key={feature.key}
                  className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1"
                  style={{ left: `${at.left}%`, top: `${at.top}%` }}
                >
                  <ChartDataPoint label={`${feature.label} · ${shown(feature)}`.trim()}>
                    <span
                      aria-hidden
                      className="block shrink-0 rounded-full"
                      style={{ width: size, height: size, background: chartAlpha(hue, '0.85') }}
                    />
                  </ChartDataPoint>
                  <span className="whitespace-nowrap text-[10px] text-content-secondary">
                    {feature.label}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── CHOROPLETH, and the pre-v2 `regions[]` ────────────────────────────────
  const ranked = [...features].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  return (
    <div className={`flex h-full items-center gap-4 ${className}`.trim()}>
      <svg width="96" height="62" viewBox="0 0 120 78" className="shrink-0" aria-hidden>
        {features.slice(0, LANDMASSES.length).map((feature, index) => (
          <path
            key={feature.key}
            d={LANDMASSES[index]}
            fill={chartAlpha(hue, (0.18 + share(feature.value ?? 0) * 0.72).toFixed(2))}
            className="wg-enter-fade"
          />
        ))}
      </svg>

      <div className="min-w-0 flex-1">
        {ranked.map((feature) => {
          const ratio = share(feature.value ?? 0);
          return (
            <ChartDataPoint
              key={feature.key}
              fill="block w-full"
              label={feature.value == null
                ? feature.label
                : `${feature.label} · ${shown(feature)} — ${Math.round(ratio * 100)}% of the strongest region`}
            >
              <div className="flex items-center gap-2 py-1">
                <span
                  aria-hidden
                  className="h-2 w-2 shrink-0 rounded-sm"
                  style={{ background: chartAlpha(hue, (feature.value == null ? 0.9 : 0.2 + ratio * 0.8).toFixed(2)) }}
                />
                <span className="min-w-0 flex-1 truncate text-[11px] text-content-muted">
                  {feature.label}
                </span>
                <span className="text-[11px] font-semibold text-content">{shown(feature)}</span>
              </div>
            </ChartDataPoint>
          );
        })}
      </div>
    </div>
  );
}

MapChartWidget.propTypes = {
  /** `MAP`'s `features[]` and `mode`, or the pre-v2 `regions[]`. */
  data: PropTypes.shape({
    mode: PropTypes.oneOf(['POINTS', 'CLUSTER', 'CHOROPLETH']),
    bounds: PropTypes.shape({
      north: PropTypes.number, south: PropTypes.number,
      east: PropTypes.number, west: PropTypes.number,
    }),
    features: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      lat: PropTypes.number,
      lng: PropTypes.number,
      region_code: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.number,
      record_uuid: PropTypes.string,
    })),
    format: PropTypes.object,
    // pre-v2
    regions: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(MapChartWidget);
